import {
    ChangeDetectorRef,
    Component,
    computed,
    OnDestroy,
    OnInit,
    signal,
    Signal,
    ViewChild,
    WritableSignal,
} from '@angular/core';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { NotificationListComponent } from '../../shared/components/notification-list/notification-list.component';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { SignalRService } from 'src/app/core/services/signalR.service';
import { TicketDetailsPanelService } from 'src/app/core/services/ticket-details-panel.service';
import { TicketNotificationsService } from 'src/app/core/services/ticket-notification.service';
import { NotificationGroupType } from 'src/app/core/enums/notification-group-type.enum';
import { EnumToArrayPipe } from '@sb-tools/typescript/pipes/enum-to-array.pipe';
import { UnreadNotificationsCountDTO } from '../../core/models/dto/unread-notifications-count.dto';
import { UnreadNotificationCountUpdatedEventModel } from '../../core/models/dto/signalR/unread-notifications-count-event-model.model';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { InlineSVGComponent } from '../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { UiSwitchModule } from 'ngx-ui-switch';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { NgClass, NgTemplateOutlet } from '@angular/common';

@Component({
    selector: 'app-notifications-dropdown',
    templateUrl: './notifications-dropdown.component.html',
    styleUrls: ['./notifications-dropdown.component.scss'],
    imports: [
        BsDropdownModule,
        InlineSVGComponent,
        UiSwitchModule,
        FormsModule,
        TranslateModule,
        RouterLink,
        TooltipModule,
        TabsModule,
        NgClass,
        NgTemplateOutlet,
        NotificationListComponent,
    ],
})
export class NotificationsDropdownComponent implements OnInit, OnDestroy {
    @ViewChild(NotificationListComponent) notificationListComponent: NotificationListComponent;

    isShowUnread = true;
    notificationGroup: NotificationGroupType = NotificationGroupType.activity;
    notificationGroupTypeEnum = NotificationGroupType;
    unreadNotificationsCount = 0;

    unreadNotificationsCountsData: WritableSignal<UnreadNotificationsCountDTO[]> = signal(null);
    unreadNotificationsCounts: Record<string, Signal<number>>;

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        private notificationService: NotificationService,
        private signalRService: SignalRService,
        private ticketDetailsPanelService: TicketDetailsPanelService,
        private activatedRoute: ActivatedRoute,
        private changeDetectorRef: ChangeDetectorRef,
        private ticketNotificationsService: TicketNotificationsService,
        private enumToArrayPipe: EnumToArrayPipe,
    ) {}

    ngOnInit(): void {
        this.loadUnreadNotificationsCount();
        this.initUnreadNotificationsCounts();
        this.loadUnreadNotificationsCountsData();
        this.initListeners();
    }

    private initListeners(): void {
        this.signalRService
            .onUnreadNotificationCountUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((count: UnreadNotificationCountUpdatedEventModel) => {
                this.unreadNotificationsCount = count.totalUnreadCount;
                this.loadUnreadNotificationsCountsData();
                this.changeDetectorRef.detectChanges();
            });
    }

    private initUnreadNotificationsCounts(): void {
        const keys = this.enumToArrayPipe.transform(NotificationGroupType);

        this.unreadNotificationsCounts = keys.reduce((res, key) => {
            res[key] = computed(() => {
                const counts = this.unreadNotificationsCountsData();
                const count = counts?.find((item) => item.groupType === key);
                return count ? count.count : null;
            });
            return res;
        }, {});
    }

    private loadUnreadNotificationsCountsData(): void {
        this.notificationService
            .getUnreadCountsForGroups(true)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((result) => {
                this.unreadNotificationsCountsData.set(result);
            });
    }

    private loadUnreadNotificationsCount(): void {
        this.notificationService
            .getUnreadCount()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((count: number) => {
                this.unreadNotificationsCount = count;
                this.changeDetectorRef.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
